import { useEffect, useMemo, useState } from 'react';
import { myInvoices, me, createCheckoutSession } from '@/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ReceiptText, CreditCard, CheckCircle2, ArrowLeft } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { useNavigate, Link } from 'react-router-dom';
import LanguageSelector from '@/components/LanguageSelector';

const LOGO_URL = '/logo.png';

function currency(n){ return `${Number(n||0).toFixed(2)} EUR`; }

export default function CompanySubscription(){
  const { t } = useLanguage();
  const nav = useNavigate();
  const [profile,setProfile]=useState(null);
  const [invoices,setInvoices]=useState([]);
  const [year,setYear]=useState(String(new Date().getFullYear()));
  const [filter,setFilter]=useState('Tutti');
  const [cancelOpen,setCancelOpen]=useState(false);
  const [confirmStep,setConfirmStep]=useState('confirm');
  const [password,setPassword]=useState('');

  useEffect(()=>{ 
    me().then(r=>setProfile(r.data)).catch(()=>{}); 
    myInvoices().then(r=>setInvoices(r.data||[])).catch(()=>{});
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    if (sessionId) {
      toast.loading('Verifica pagamento in corso...', { id: 'payment-check' });
      window.history.replaceState({}, '', '/ditta/abbonamento');
      import('@/api').then(({ getCheckoutStatus }) => {
        getCheckoutStatus(sessionId).then(() => {
          setTimeout(() => { me().then(r => { setProfile(r.data); toast.dismiss('payment-check'); toast.success(r.data?.plan?.status === 'active' ? 'Pagamento completato! Abbonamento attivato' : 'Pagamento verificato con successo'); }); }, 1000);
        }).catch(() => { toast.dismiss('payment-check'); toast.error('Errore durante la verifica del pagamento'); });
      });
    }
  },[]);

  const currentMonthKey = useMemo(()=> new Date().toISOString().slice(0,7), []);
  const contactsThisMonth = useMemo(()=> invoices.filter(i=> i.type==='contact' && String(i.createdAt||'').startsWith(currentMonthKey)), [invoices, currentMonthKey]);
  const contactsSum = useMemo(()=> contactsThisMonth.reduce((s,i)=> s+Number(i.amount||0), 0), [contactsThisMonth]);
  const baseMonthly = Number(profile?.plan?.monthlyPrice ?? 29.90);
  const totalEst = (baseMonthly + contactsSum);

  const years = useMemo(()=>{ const ys = new Set([String(new Date().getFullYear())]); invoices.forEach(i=>{ const y=(i.createdAt||'').slice(0,4); if(y) ys.add(y); }); return Array.from(ys).sort((a,b)=> Number(b)-Number(a)); },[invoices]);

  const filteredInvoices = useMemo(()=> invoices.filter(i=> { const y = (i.createdAt||'').slice(0,4); const okY = year? (y===year) : true; const okF = filter==='Tutti' ? true : (i.type===filter.toLowerCase()); return okY && okF; }),[invoices, year, filter]);

  const doCancel = async () => {
    if(password.length < 4){ toast.error(t('subscription.enterPassword')); return; }
    try {
      const token = sessionStorage.getItem('jc_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/subscription/manage`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify({ action: 'cancel' }) });
      if (!response.ok) throw new Error('Errore annullamento');
      const data = await response.json();
      setProfile(p => p ? ({...p, plan: data.plan}) : p);
      setCancelOpen(false); setConfirmStep('confirm'); setPassword('');
      toast.success(t('subscription.subscriptionCanceled'));
    } catch (err) { toast.error('Errore durante l\'annullamento'); }
  };
  
  const doActivate = async () => {
    try {
      const response = await createCheckoutSession('subscription', { reason: 'reactivation' });
      if (response.data?.url) window.location.href = response.data.url;
      else throw new Error('URL checkout non ricevuto');
    } catch (err) { toast.error('Errore durante la creazione della sessione di pagamento'); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster />
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <button onClick={() => nav('/ditta/dashboard')} className="p-2 hover:bg-slate-100 rounded-lg"><ArrowLeft className="w-5 h-5 text-slate-600" /></button>
              <Link to="/"><img src={LOGO_URL} alt="ServiceGo" className="h-10 w-auto" /></Link>
            </div>
            <div className="flex items-center gap-3">
              <LanguageSelector />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button data-testid="manage-subscription" className="rounded-xl bg-[#5848FF] hover:bg-[#4638EF] text-white">{t('subscription.manage')}</Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-white rounded-xl shadow-xl" align="end" data-testid="manage-menu">
                  {profile?.plan?.status === 'canceled' ? (
                    <DropdownMenuItem data-testid="manage-activate" onClick={doActivate}>{t('subscription.activateSubscription')}</DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem data-testid="manage-cancel" onClick={()=>{ setCancelOpen(true); setConfirmStep('confirm'); }}>{t('subscription.cancelSubscription')}</DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="company-subscription-page">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">{t('subscription.title')}</h1>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Subscription Card */}
            <div className="rounded-2xl p-6 text-white relative overflow-hidden" style={{background: 'linear-gradient(135deg, #4F46E5 0%, #3B82F6 100%)'}}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-indigo-200 text-sm">{profile?.plan?.status==='canceled' ? t('subscription.subscriptionCanceled') : t('subscription.subscriptionActive')}</p>
                  <div className="mt-2 text-4xl font-extrabold">{baseMonthly.toFixed(2)} EUR</div>
                  <p className="text-indigo-200 text-sm mt-1">{t('subscription.perMonth')}</p>
                </div>
                <div className="h-14 w-14 rounded-2xl bg-white/20 flex items-center justify-center"><ReceiptText className="w-7 h-7 text-white" /></div>
              </div>
            </div>

            {/* Invoices */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-slate-900">{t('ui.invoices')}</h2>
                <div className="flex gap-2">
                  <Select value={year} onValueChange={setYear}>
                    <SelectTrigger data-testid="invoice-year" className="h-10 rounded-xl w-28"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white rounded-xl">{years.map(y=> <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={filter} onValueChange={setFilter}>
                    <SelectTrigger data-testid="invoice-filter" className="h-10 rounded-xl w-36"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white rounded-xl">
                      <SelectItem value="Tutti">{t('ui.allFilter')}</SelectItem>
                      <SelectItem value="subscription">{t('ui.subscriptionFilter')}</SelectItem>
                      <SelectItem value="contact">{t('ui.contactFilter')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-3">
                {filteredInvoices.map(inv=>{
                  const date = (inv.createdAt||'').slice(0,10);
                  const title = inv.type==='subscription' ? `${t('ui.subscriptionInvoice')} ${date}` : `${t('ui.contactUnlocked')} ${date}`;
                  return (
                    <div key={inv.id} className="flex items-center gap-3 p-4 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors" data-testid={`invoice-${inv.id}`}>
                      <div className="h-10 w-10 rounded-xl bg-indigo-100 flex items-center justify-center"><CreditCard className="w-5 h-5 text-[#5848FF]" /></div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-900 truncate">{title}</div>
                        <div className="text-xs text-slate-500">{inv.currency || 'EUR'} - {inv.status}</div>
                      </div>
                      <div className="text-sm font-semibold text-slate-800">{currency(inv.amount)}</div>
                      {inv.status==='paid' && <CheckCircle2 className="w-5 h-5 text-emerald-500" />}
                    </div>
                  );
                })}
                {!filteredInvoices.length && <p className="text-sm text-slate-500 py-4 text-center" data-testid="invoices-empty">{t('ui.noInvoices')}</p>}
              </div>
            </div>
          </div>

          <div>
            {/* Monthly Summary */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sticky top-28">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">{t('subscription.currentMonthSummary')}</h2>
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm text-slate-700"><span>{t('subscription.baseSubscription')}</span><span className="font-medium">{currency(baseMonthly)}</span></div>
                <div className="flex items-center justify-between text-sm text-slate-700"><span>{t('subscription.unlockedContacts')} ({contactsThisMonth.length})</span><span className="font-medium">{currency(contactsSum)}</span></div>
                <div className="border-t border-slate-200 pt-3 flex items-center justify-between font-semibold"><span>{t('subscription.estimatedTotal')}</span><span className="text-[#5848FF] text-lg">{currency(totalEst)}</span></div>
              </div>
              <p className="text-xs text-slate-500 mt-4">{t('ui.invoiceWillBeGenerated')}</p>
            </div>
          </div>
        </div>
      </main>

      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent className="rounded-2xl bg-white" data-testid="cancel-dialog">
          <DialogHeader><DialogTitle>{t('ui.cancelSubscription')}</DialogTitle></DialogHeader>
          {confirmStep==='confirm' ? (
            <div className="space-y-4">
              <p className="text-sm text-slate-700">{t('ui.cancelConfirm')}</p>
              <div className="flex justify-end gap-2">
                <Button data-testid="cancel-dialog-close" variant="outline" onClick={()=>setCancelOpen(false)} className="rounded-xl">{t('ui.cancel')}</Button>
                <Button data-testid="cancel-dialog-yes" className="bg-[#5848FF] text-white rounded-xl" onClick={()=>setConfirmStep('password')}>{t('ui.yesImSure')}</Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-slate-700">{t('ui.enterPasswordToConfirm')}</p>
              <Input data-testid="cancel-password" type="password" placeholder="Password" value={password} onChange={e=>setPassword(e.target.value)} className="h-11 rounded-xl" />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={()=>{ setConfirmStep('confirm'); setPassword(''); }} className="rounded-xl">{t('ui.back')}</Button>
                <Button data-testid="cancel-confirm" className="bg-[#5848FF] text-white rounded-xl" onClick={doCancel}>{t('ui.confirm')}</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
